import { useMemo } from "react";

export type AppRole = "admin" | "vendedor" | "instalador" | "entregador" | "financeiro" | "administrativo";
export type ModulePermissions = Partial<Record<"crm" | "financeiro" | "instalacoes" | "configuracoes" | "documentos" | "logistica" | "settle_financial" | "generate_report" | "configure_automations", boolean>>;

export interface RolePermissions {
  canViewDashboard: boolean;
  canViewCRM: boolean;
  canViewClientes: boolean;
  canViewInstalacoes: boolean;
  canViewFinanceiro: boolean;
  canViewDocumentos: boolean;
  canViewConfiguracoes: boolean;
  canViewStatus: boolean;
  canViewLogistica: boolean;
  canViewIntegracoes: boolean;
  canManageUsers: boolean;
  canManageTenant: boolean;
  canCreateLead: boolean;
  canEditLead: boolean;
  canDeleteLead: boolean;
  canCreateDeal: boolean;
  canEditDeal: boolean;
  canDeleteDeal: boolean;
  canCreateTask: boolean;
  canEditTask: boolean;
  canDeleteTask: boolean;
  canCreatePayable: boolean;
  canEditPayable: boolean;
  canDeletePayable: boolean;
  canCreateReceivable: boolean;
  canEditReceivable: boolean;
  canDeleteReceivable: boolean;
  canEditInstallation: boolean;
  canSettleFinancial: boolean;
  canGenerateReport: boolean;
  canConfigureAutomations: boolean;
  canManageCampaigns: boolean;
}

const rolePermissionsMap: Record<AppRole, RolePermissions> = {
  admin: {
    canViewDashboard: true, canViewCRM: true, canViewClientes: true, canViewInstalacoes: true,
    canViewFinanceiro: true, canViewDocumentos: true, canViewConfiguracoes: true, canViewStatus: true,
    canViewLogistica: true, canViewIntegracoes: true, canManageUsers: true, canManageTenant: true,
    canCreateLead: true, canEditLead: true, canDeleteLead: true,
    canCreateDeal: true, canEditDeal: true, canDeleteDeal: true,
    canCreateTask: true, canEditTask: true, canDeleteTask: true,
    canCreatePayable: true, canEditPayable: true, canDeletePayable: true,
    canCreateReceivable: true, canEditReceivable: true, canDeleteReceivable: true,
    canEditInstallation: true,
    canSettleFinancial: true, canGenerateReport: true, canConfigureAutomations: true,
    canManageCampaigns: true,
  },
  vendedor: {
    canViewDashboard: true, canViewCRM: true, canViewClientes: true, canViewInstalacoes: false,
    canViewFinanceiro: false, canViewDocumentos: true, canViewConfiguracoes: true, canViewStatus: true,
    canViewLogistica: false, canViewIntegracoes: false, canManageUsers: false, canManageTenant: false,
    canCreateLead: true, canEditLead: true, canDeleteLead: false,
    canCreateDeal: true, canEditDeal: true, canDeleteDeal: false,
    canCreateTask: true, canEditTask: true, canDeleteTask: false,
    canCreatePayable: false, canEditPayable: false, canDeletePayable: false,
    canCreateReceivable: false, canEditReceivable: false, canDeleteReceivable: false,
    canEditInstallation: false,
    canSettleFinancial: false, canGenerateReport: false, canConfigureAutomations: false,
    canManageCampaigns: false,
  },
  instalador: {
    canViewDashboard: true, canViewCRM: false, canViewClientes: false, canViewInstalacoes: true,
    canViewFinanceiro: false, canViewDocumentos: false, canViewConfiguracoes: true, canViewStatus: true,
    canViewLogistica: false, canViewIntegracoes: false, canManageUsers: false, canManageTenant: false,
    canCreateLead: false, canEditLead: false, canDeleteLead: false,
    canCreateDeal: false, canEditDeal: false, canDeleteDeal: false,
    canCreateTask: false, canEditTask: false, canDeleteTask: false,
    canCreatePayable: false, canEditPayable: false, canDeletePayable: false,
    canCreateReceivable: false, canEditReceivable: false, canDeleteReceivable: false,
    canEditInstallation: true,
    canSettleFinancial: false, canGenerateReport: false, canConfigureAutomations: false,
    canManageCampaigns: false,
  },
  entregador: {
    canViewDashboard: true, canViewCRM: false, canViewClientes: false, canViewInstalacoes: false,
    canViewFinanceiro: false, canViewDocumentos: false, canViewConfiguracoes: false, canViewStatus: true,
    canViewLogistica: true, canViewIntegracoes: false, canManageUsers: false, canManageTenant: false,
    canCreateLead: false, canEditLead: false, canDeleteLead: false,
    canCreateDeal: false, canEditDeal: false, canDeleteDeal: false,
    canCreateTask: false, canEditTask: false, canDeleteTask: false,
    canCreatePayable: false, canEditPayable: false, canDeletePayable: false,
    canCreateReceivable: false, canEditReceivable: false, canDeleteReceivable: false,
    canEditInstallation: false,
    canSettleFinancial: false, canGenerateReport: false, canConfigureAutomations: false,
    canManageCampaigns: false,
  },
  financeiro: {
    canViewDashboard: true, canViewCRM: false, canViewClientes: false, canViewInstalacoes: false,
    canViewFinanceiro: true, canViewDocumentos: true, canViewConfiguracoes: true, canViewStatus: true,
    canViewLogistica: false, canViewIntegracoes: false, canManageUsers: false, canManageTenant: false,
    canCreateLead: false, canEditLead: false, canDeleteLead: false,
    canCreateDeal: false, canEditDeal: false, canDeleteDeal: false,
    canCreateTask: false, canEditTask: false, canDeleteTask: false,
    canCreatePayable: true, canEditPayable: true, canDeletePayable: false,
    canCreateReceivable: true, canEditReceivable: true, canDeleteReceivable: false,
    canEditInstallation: false,
    canSettleFinancial: true, canGenerateReport: false, canConfigureAutomations: false,
    canManageCampaigns: false,
  },
  administrativo: {
    canViewDashboard: true, canViewCRM: false, canViewClientes: true, canViewInstalacoes: false,
    canViewFinanceiro: false, canViewDocumentos: true, canViewConfiguracoes: true, canViewStatus: true,
    canViewLogistica: false, canViewIntegracoes: false, canManageUsers: false, canManageTenant: false,
    canCreateLead: false, canEditLead: false, canDeleteLead: false,
    canCreateDeal: false, canEditDeal: false, canDeleteDeal: false,
    canCreateTask: false, canEditTask: false, canDeleteTask: false,
    canCreatePayable: false, canEditPayable: false, canDeletePayable: false,
    canCreateReceivable: false, canEditReceivable: false, canDeleteReceivable: false,
    canEditInstallation: false,
    canSettleFinancial: false, canGenerateReport: false, canConfigureAutomations: false,
    canManageCampaigns: false,
  },
};

function applyModuleOverrides(base: RolePermissions, modulePermissions?: ModulePermissions | null): RolePermissions {
  if (!modulePermissions || Object.keys(modulePermissions).length === 0) return base;

  const next = { ...base };

  if (modulePermissions.crm === false) {
    next.canViewCRM = false;
    next.canCreateLead = false;
    next.canEditLead = false;
    next.canDeleteLead = false;
    next.canCreateDeal = false;
    next.canEditDeal = false;
    next.canDeleteDeal = false;
  }

  if (modulePermissions.financeiro === false) {
    next.canViewFinanceiro = false;
    next.canCreatePayable = false;
    next.canEditPayable = false;
    next.canDeletePayable = false;
    next.canCreateReceivable = false;
    next.canEditReceivable = false;
    next.canDeleteReceivable = false;
  }

  if (modulePermissions.instalacoes === false) {
    next.canViewInstalacoes = false;
    next.canEditInstallation = false;
  }

  if (modulePermissions.configuracoes === false) {
    next.canViewConfiguracoes = false;
    next.canManageUsers = false;
    next.canManageTenant = false;
  }

  if (modulePermissions.documentos === false) next.canViewDocumentos = false;
  if (modulePermissions.logistica === false) next.canViewLogistica = false;

  // Granular permission overrides
  if (modulePermissions.settle_financial === true) next.canSettleFinancial = true;
  if (modulePermissions.settle_financial === false) next.canSettleFinancial = false;
  if (modulePermissions.generate_report === true) next.canGenerateReport = true;
  if (modulePermissions.generate_report === false) next.canGenerateReport = false;
  if (modulePermissions.configure_automations === true) next.canConfigureAutomations = true;
  if (modulePermissions.configure_automations === false) next.canConfigureAutomations = false;

  return next;
}

export function useRolePermissions(role: AppRole | null, modulePermissions?: ModulePermissions | null): RolePermissions {
  return useMemo(() => {
    const base = role
      ? rolePermissionsMap[role] || rolePermissionsMap.administrativo
      : {
          canViewDashboard: true, canViewCRM: false, canViewClientes: false,
          canViewInstalacoes: false, canViewFinanceiro: false, canViewDocumentos: false,
          canViewConfiguracoes: true, canViewStatus: false, canViewLogistica: false,
          canViewIntegracoes: false, canManageUsers: false, canManageTenant: false,
          canCreateLead: false, canEditLead: false, canDeleteLead: false,
          canCreateDeal: false, canEditDeal: false, canDeleteDeal: false,
          canCreateTask: false, canEditTask: false, canDeleteTask: false,
          canCreatePayable: false, canEditPayable: false, canDeletePayable: false,
          canCreateReceivable: false, canEditReceivable: false, canDeleteReceivable: false,
          canEditInstallation: false,
          canSettleFinancial: false, canGenerateReport: false, canConfigureAutomations: false,
          canManageCampaigns: false,
        };

    return applyModuleOverrides(base, modulePermissions);
  }, [modulePermissions, role]);
}

export function getRoleLabel(role: AppRole | string | null): string {
  const labels: Record<string, string> = {
    admin: "Administrador",
    vendedor: "Vendedor",
    instalador: "Instalador",
    entregador: "Entregador (Logística)",
    financeiro: "Financeiro",
    administrativo: "Administrativo",
  };
  return role ? labels[role] || role : "Sem função";
}
